import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  PencilIcon,
  UserPlusIcon,
  TrashIcon,
  GlobeAltIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { useCompanyHasModule } from "../../../../routes/roleOnly";
import { fetchCompany } from "../../../../api/company";
import { publicJobOpeningUrl } from "../../../../utils/jobOpeningWebsite";
import {
  requirementChecksFromSource,
  requirementItemsFromOpening,
} from "../../../../utils/jobOpeningRequirements";
import {
  RequirementChecksEditor,
  RequirementItemsEditor,
  RequirementItemsList,
} from "../../../../components/dashboard/hr/RequirementChecklist";
import {
  deleteJobOpening,
  fetchJobOpening,
  updateJobOpening,
} from "../../../../api/jobOpenings";
import { createJobApplication, fetchJobApplications } from "../../../../api/jobApplications";
import { StatusUpdateSection } from "../../../../components/dashboard/statusUpdates/StatusUpdateSection";
import JobOpeningContactPicker, {
  contactUserFromOpening,
  contactUserIdFrom,
} from "../../../../components/dashboard/hr/JobOpeningContactPicker";
import {
  candidatePagePath,
  DetailRow,
  SectionCard,
  STATUS_STYLES,
} from "./shared";

export const JobOpeningShow = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const websiteModuleActive = useCompanyHasModule("website");
  const [opening, setOpening] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(searchParams.get("edit") === "1");
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [websiteSaving, setWebsiteSaving] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);

  const loadOpening = useCallback(async () => {
    const response = await fetchJobOpening(id);
    if (response?.id) setOpening(response);
    else toast.error(t("hr.job_openings.load_failed"));
  }, [id, t]);

  const loadCandidates = useCallback(async () => {
    const response = await fetchJobApplications({
      job_opening_id: id,
      per_page: 100,
      sortBy: "applied_at",
      sort: "DESC",
    });
    setCandidates(response?.data || []);
  }, [id]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([loadOpening(), loadCandidates()]);
      setIsLoading(false);
    })();
  }, [loadOpening, loadCandidates]);

  useEffect(() => {
    if (!websiteModuleActive) {
      setCompanySettings(null);
      return undefined;
    }
    let cancelled = false;
    fetchCompany({ fields: "id,web,webshop_products_url" })
      .then((res) => {
        if (cancelled) return;
        setCompanySettings(res?.data || res || null);
      })
      .catch(() => {
        if (!cancelled) setCompanySettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [websiteModuleActive]);

  const publicUrl = publicJobOpeningUrl(companySettings, opening?.slug);
  const canOpenPublicPage =
    websiteModuleActive &&
    !!publicUrl &&
    opening?.status === "published" &&
    !!opening?.show_on_website;

  const handleToggleShowOnWebsite = async () => {
    if (!websiteModuleActive || websiteSaving || !opening?.id) return;
    setWebsiteSaving(true);
    try {
      const response = await updateJobOpening({
        id: opening.id,
        show_on_website: !opening.show_on_website,
      });
      if (response?.errors || response?.error || !response?.id) {
        toast.error(t("hr.job_openings.update_failed"));
        return;
      }
      setOpening(response);
      toast.success(
        response.show_on_website
          ? t("hr.job_openings.website_shown")
          : t("hr.job_openings.website_hidden")
      );
    } finally {
      setWebsiteSaving(false);
    }
  };

  useEffect(() => {
    const candidateId = searchParams.get("candidate");
    if (!candidateId) return;
    navigate(candidatePagePath(id, candidateId), { replace: true });
  }, [id, navigate, searchParams]);

  const handleSaveOpening = async (values, helpers) => {
    const requirement_items = (values.requirement_items || [])
      .map((item) => ({ id: item.id, text: String(item.text || "").trim() }))
      .filter((item) => item.text);
    const { contact_user, ...rest } = values;
    const response = await updateJobOpening({
      ...rest,
      requirement_items,
      id,
      contact_user_id: contactUserIdFrom(contact_user) ?? rest.contact_user_id ?? null,
    });
    if (response?.errors || response?.error) {
      toast.error(t("hr.job_openings.update_failed"));
      helpers.setSubmitting(false);
      return;
    }
    setOpening(response);
    setEditOpen(false);
    setSearchParams({});
    toast.success(t("hr.job_openings.updated"));
  };

  const handleDeleteOpening = async () => {
    if (!window.confirm(t("hr.job_openings.delete_confirm"))) return;
    const response = await deleteJobOpening(id);
    if (response?.success) {
      toast.success(t("hr.job_openings.deleted"));
      navigate("/dashboard/hr/job-openings");
    } else {
      toast.error(t("hr.job_openings.delete_failed"));
    }
  };

  const handleCreateCandidate = async (values, helpers) => {
    const requirement_checks = (values.requirement_checks || [])
      .map((item) => ({
        id: item.id,
        text: String(item.text || "").trim(),
        checked: !!item.checked,
      }))
      .filter((item) => item.text);
    const response = await createJobApplication({
      ...values,
      requirement_checks,
      job_opening_id: Number(id),
      source: "manual",
      consent_privacy: true,
    });
    if (response?.errors || response?.error) {
      toast.error(t("hr.candidates.create_failed"));
      helpers.setSubmitting(false);
      return;
    }
    toast.success(t("hr.candidates.created"));
    setCandidateDialogOpen(false);
    helpers.resetForm();
    await loadCandidates();
    await loadOpening();
    navigate(candidatePagePath(id, response.id));
  };

  if (isLoading || !opening) {
    return (
      <div className="py-20">
        <ResourceLoading />
      </div>
    );
  }

  return (
    <>
      <DashboardHeader
        variant="v2"
        title={opening.title}
        subtitle={opening.short_description}
        backUrl="/dashboard/hr/job-openings"
        meta={[
          {
            label: t("hr.job_openings.status"),
            value:
              opening.status_label ||
              t(`hr.job_openings.statuses.${opening.status}`),
            hidden: false,
          },
        ]}
        actions={[
          {
            label: opening.show_on_website
              ? t("hr.job_openings.hide_from_website")
              : t("hr.job_openings.fields.show_on_website"),
            icon: GlobeAltIcon,
            action: handleToggleShowOnWebsite,
            hidden: !websiteModuleActive,
            disabled: websiteSaving,
            menu: false,
          },
          {
            label: t("hr.job_openings.open_on_website"),
            icon: ArrowTopRightOnSquareIcon,
            action: () =>
              window.open(publicUrl, "_blank", "noopener,noreferrer"),
            hidden: !canOpenPublicPage,
            menu: false,
          },
          {
            label: t("hr.common.edit"),
            icon: PencilIcon,
            action: () => setEditOpen(true),
            hidden: false,
            menu: false,
          },
          {
            label: t("hr.common.delete"),
            icon: TrashIcon,
            action: handleDeleteOpening,
            hidden: false,
            menu: true,
          },
        ]}
      >
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <SectionCard title={t("hr.job_openings.sections.overview")}>
            <dl>
              <DetailRow
                label={t("hr.job_openings.fields.location")}
                value={opening.location}
              />
              <DetailRow
                label={t("hr.job_openings.fields.employment_type")}
                value={
                  opening.employment_type_label ||
                  t(`hr.job_openings.employment_types.${opening.employment_type}`)
                }
              />
              <DetailRow
                label={t("hr.job_openings.fields.department")}
                value={opening.department_name}
              />
              <DetailRow
                label={t("hr.job_openings.fields.work_percentage")}
                value={
                  opening.work_percentage
                    ? `${opening.work_percentage} %`
                    : null
                }
              />
              <DetailRow
                label={t("hr.job_openings.fields.salary_info")}
                value={opening.salary_info}
              />
              <DetailRow
                label={t("hr.job_openings.fields.deadline")}
                value={opening.application_deadline}
              />
              <DetailRow
                label={t("hr.job_openings.fields.starts_on")}
                value={opening.starts_on}
              />
              {websiteModuleActive && (
                <div className="grid grid-cols-[140px_1fr] gap-3 py-2 text-sm">
                  <dt className="font-semibold text-slate-500">
                    {t("hr.job_openings.fields.show_on_website")}
                  </dt>
                  <dd className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      disabled={websiteSaving}
                      onClick={handleToggleShowOnWebsite}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-60 ${
                        opening.show_on_website
                          ? "bg-sky-50 text-sky-800"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      <GlobeAltIcon className="h-3.5 w-3.5" />
                      {opening.show_on_website
                        ? t("hr.common.yes")
                        : t("hr.common.no")}
                    </button>
                    {canOpenPublicPage && (
                      <a
                        href={publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 font-semibold text-[#1463C7]"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        {t("hr.job_openings.open_on_website")}
                      </a>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </SectionCard>

          <SectionCard title={t("hr.job_openings.sections.description")}>
            <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {opening.description || t("hr.common.empty")}
            </p>
          </SectionCard>

          <SectionCard title={t("hr.job_openings.sections.responsibilities")}>
            <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {opening.responsibilities || t("hr.common.empty")}
            </p>
          </SectionCard>

          <SectionCard title={t("hr.job_openings.sections.requirements")}>
            <RequirementItemsList
              items={requirementItemsFromOpening(opening)}
              emptyLabel={t("hr.common.empty")}
            />
          </SectionCard>

          <SectionCard title={t("hr.job_openings.sections.benefits")}>
            <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {opening.benefits || t("hr.common.empty")}
            </p>
          </SectionCard>

          <SectionCard
            title={t("hr.candidates.title")}
            action={
              <button
                type="button"
                onClick={() => setCandidateDialogOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#1463C7] px-3 py-1.5 text-xs font-semibold text-white"
              >
                <UserPlusIcon className="h-4 w-4" />
                {t("hr.candidates.add")}
              </button>
            }
          >
            {candidates.length === 0 ? (
              <p className="text-sm text-slate-500">
                {t("hr.candidates.empty")}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {candidates.map((candidate) => (
                  <li
                    key={candidate.id}
                    className="flex cursor-pointer items-center justify-between gap-3 py-3 hover:bg-slate-50"
                    onClick={() => navigate(candidatePagePath(id, candidate.id))}
                  >
                    <div>
                      <div className="font-semibold text-slate-900">
                        {candidate.full_name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {candidate.email}
                        {candidate.phone ? ` · ${candidate.phone}` : ""}
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        STATUS_STYLES[candidate.status] || STATUS_STYLES.new
                      }`}
                    >
                      {candidate.status_label ||
                        t(`hr.candidates.statuses.${candidate.status}`)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <section className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,.04)]">
            <p className="mb-3 text-sm text-slate-500">
              {t("hr.job_openings.sections.history_hint")}
            </p>
            <StatusUpdateSection
              subjectType="job_opening"
              subjectId={opening.id}
              title={t("hr.job_openings.sections.history")}
            />
          </section>
        </div>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
          <SectionCard title={t("hr.job_openings.sections.contact")}>
            <dl>
              <DetailRow
                label={t("hr.job_openings.fields.contact_user")}
                value={
                  opening.contact_user?.displayTitle ||
                  opening.contact_user?.name ||
                  opening.contact_name
                }
              />
              <DetailRow
                label={t("hr.job_openings.fields.contact_email")}
                value={opening.contact_user?.email || opening.contact_email}
              />
              <DetailRow
                label={t("hr.job_openings.fields.contact_phone")}
                value={
                  opening.contact_user?.mobile_work ||
                  opening.contact_user?.mobile ||
                  opening.contact_phone
                }
              />
            </dl>
          </SectionCard>

          <SectionCard title={t("hr.candidates.pipeline")}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">{t("hr.candidates.total")}</span>
                <span className="font-semibold">{candidates.length}</span>
              </div>
              {["new", "reviewing", "interview", "offer", "hired"].map(
                (status) => (
                  <div key={status} className="flex justify-between">
                    <span className="text-slate-500">
                      {t(`hr.candidates.statuses.${status}`)}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {candidates.filter((c) => c.status === status).length}
                    </span>
                  </div>
                )
              )}
            </div>
          </SectionCard>
        </aside>
          </div>
        </div>
      </DashboardHeader>

      {/* Edit opening */}
      <PMDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSearchParams({});
        }}
        title={t("hr.job_openings.edit")}
      >
        <Formik
          enableReinitialize
          initialValues={{
            title: opening.title || "",
            slug: opening.slug || "",
            short_description: opening.short_description || "",
            description: opening.description || "",
            responsibilities: opening.responsibilities || "",
            requirement_items: requirementItemsFromOpening(opening),
            benefits: opening.benefits || "",
            location: opening.location || "",
            employment_type: opening.employment_type || "full_time",
            department_name: opening.department_name || "",
            salary_info: opening.salary_info || "",
            work_percentage: opening.work_percentage || "",
            positions_count: opening.positions_count || 1,
            application_deadline: opening.application_deadline || "",
            starts_on: opening.starts_on || "",
            contact_user: contactUserFromOpening(opening),
            contact_user_id: opening.contact_user_id || contactUserIdFrom(opening.contact_user) || null,
            status: opening.status || "draft",
            show_on_website: !!opening.show_on_website,
          }}
          validationSchema={Yup.object({
            title: Yup.string().required(t("hr.job_openings.validation.title")),
          })}
          onSubmit={handleSaveOpening}
        >
          {(formik) => (
            <Form className="mt-4 max-h-[70vh] space-y-4 overflow-y-auto pr-1">
              <PMField name="title" label={t("hr.job_openings.fields.title")} {...formik} />
              <PMField name="short_description" label={t("hr.job_openings.fields.short_description")} multiline rows={2} {...formik} />
              <PMField name="description" label={t("hr.job_openings.fields.description")} multiline rows={4} {...formik} />
              <PMField name="responsibilities" label={t("hr.job_openings.fields.responsibilities")} multiline rows={3} {...formik} />
              <RequirementItemsEditor
                label={t("hr.job_openings.fields.requirements")}
                hint={t("hr.job_openings.fields.requirements_hint")}
                addLabel={t("hr.job_openings.fields.requirements_add")}
                items={formik.values.requirement_items}
                onChange={(items) => formik.setFieldValue("requirement_items", items)}
              />
              <PMField name="benefits" label={t("hr.job_openings.fields.benefits")} multiline rows={3} {...formik} />
              <JobOpeningContactPicker
                value={formik.values.contact_user}
                onChange={(user) => {
                  formik.setFieldValue("contact_user", user);
                  formik.setFieldValue("contact_user_id", contactUserIdFrom(user));
                }}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <PMField name="location" label={t("hr.job_openings.fields.location")} {...formik} />
                <PMField name="department_name" label={t("hr.job_openings.fields.department")} {...formik} />
                <PMField name="salary_info" label={t("hr.job_openings.fields.salary_info")} {...formik} />
                <PMField name="work_percentage" type="number" label={t("hr.job_openings.fields.work_percentage")} {...formik} />
                <PMField name="application_deadline" type="date" label={t("hr.job_openings.fields.deadline")} {...formik} />
                <PMField name="starts_on" type="date" label={t("hr.job_openings.fields.starts_on")} {...formik} />
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">
                    {t("hr.job_openings.fields.employment_type")}
                  </label>
                  <select
                    name="employment_type"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={formik.values.employment_type}
                    onChange={formik.handleChange}
                  >
                    {["full_time", "part_time", "temporary", "internship", "freelance"].map(
                      (type) => (
                        <option key={type} value={type}>
                          {t(`hr.job_openings.employment_types.${type}`)}
                        </option>
                      )
                    )}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">
                    {t("hr.job_openings.status")}
                  </label>
                  <select
                    name="status"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                  >
                    {["draft", "published", "closed", "archived"].map((status) => (
                      <option key={status} value={status}>
                        {t(`hr.job_openings.statuses.${status}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {websiteModuleActive && (
                <PMSwitch
                  label={t("hr.job_openings.fields.show_on_website")}
                  name="show_on_website"
                  {...formik}
                />
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                  onClick={() => setEditOpen(false)}
                >
                  {t("hr.common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {t("hr.common.save")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </PMDialog>

      {/* Add candidate */}
      <PMDialog
        open={candidateDialogOpen}
        onClose={() => setCandidateDialogOpen(false)}
        title={t("hr.candidates.add")}
      >
        <Formik
          enableReinitialize
          initialValues={{
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            city: "",
            cover_letter: "",
            linkedin_url: "",
            notes: "",
            requirement_checks: requirementChecksFromSource(opening),
          }}
          validationSchema={Yup.object({
            first_name: Yup.string().required(t("hr.candidates.validation.first_name")),
            last_name: Yup.string().required(t("hr.candidates.validation.last_name")),
            email: Yup.string()
              .email(t("hr.candidates.validation.email"))
              .required(t("hr.candidates.validation.email")),
          })}
          onSubmit={handleCreateCandidate}
        >
          {(formik) => (
            <Form className="mt-4 max-h-[70vh] space-y-4 overflow-y-auto pr-1">
              <div className="grid gap-4 sm:grid-cols-2">
                <PMField name="first_name" label={t("hr.candidates.fields.first_name")} {...formik} />
                <PMField name="last_name" label={t("hr.candidates.fields.last_name")} {...formik} />
                <PMField name="email" label={t("hr.candidates.fields.email")} {...formik} />
                <PMField name="phone" label={t("hr.candidates.fields.phone")} {...formik} />
                <PMField name="city" label={t("hr.candidates.fields.city")} {...formik} />
                <PMField name="linkedin_url" label={t("hr.candidates.fields.linkedin")} {...formik} />
              </div>
              <PMField name="cover_letter" label={t("hr.candidates.fields.cover_letter")} multiline rows={4} {...formik} />
              <RequirementChecksEditor
                label={t("hr.candidates.fields.requirements")}
                hint={t("hr.candidates.fields.requirements_toggle_hint")}
                items={formik.values.requirement_checks}
                onChange={(items) => formik.setFieldValue("requirement_checks", items)}
                editable
              />
              <PMField
                name="notes"
                label={t("hr.candidates.fields.legacy_note")}
                description="hr.candidates.fields.notes_hint"
                multiline
                rows={3}
                {...formik}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                  onClick={() => setCandidateDialogOpen(false)}
                >
                  {t("hr.common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {t("hr.common.create")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </PMDialog>
    </>
  );
};
